import { useEffect, useRef, useState, type ReactElement } from 'react';
import { toast } from '../../../../design-system/components';
import { trackFailed } from '../../../../../../shared-web/track';
import { apiFailureReason, emberApi, isAuthRequired, type GroupsList } from '../../api';
import { BottomSheet } from '../../components/BottomSheet';
import { Link } from '../../components/Link';
import { PageFailure } from '../../components/PageFailure';
import { PageLoading } from '../../components/PageLoading';
import { plural } from '../../format';
import { navigate } from '../../router';
import { CHEVRON, FriendsBar } from '../friends/friend-parts';
import { GROUP_TINTS, GroupGlyph, LOCK, useGroupsLive } from './group-parts';

/**
 * Groups — `/groups` (2026-11-03 founder; mock Groups / Locked). The groups open to them, each to join (no approval) or,
 * once in, how many posts are new to them; and the groups not open to them yet, by name: a tap says how one opens
 * (they tell Ember what they have been through). Someone with no group open to them gets a short word and the way back.
 */
export function GroupsPage(): ReactElement {
  const [list, setList] = useState<GroupsList | null>(null);
  const [failed, setFailed] = useState(false);
  const [locked, setLocked] = useState<{ id: string; name: string } | null>(null);
  const [joining, setJoining] = useState('');

  const reads = useRef(0);
  const load = (): void => {
    const seq = ++reads.current;
    emberApi.groups()
      .then((r) => { if (seq === reads.current) { setList(r); setFailed(false); } })
      .catch((err: unknown) => {
        if (seq !== reads.current) return;
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent('/groups')}`, { replace: true }); return; }
        trackFailed('group_list', apiFailureReason(err));
        setFailed(true);
      });
  };
  useEffect(load, []);
  useGroupsLive(load);

  const join = async (groupId: string): Promise<void> => {
    if (joining) return;
    setJoining(groupId);
    try {
      await emberApi.joinGroup(groupId);
      navigate(`/groups/${encodeURIComponent(groupId)}`);
    } catch (err) {
      if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent('/groups')}`, { replace: true }); return; }
      trackFailed('group_join', apiFailureReason(err), { match_id: groupId });
      toast.error('Couldn’t join just now. Try again?');
      load();
    } finally {
      setJoining('');
    }
  };

  if (!list && failed) return <PageFailure title="Couldn’t open this page" logState="load_failed" retry />;
  if (!list) return <PageLoading />;

  if (!list.open) {
    return (
      <main className="nf-page gr-page" data-log-seen="group_list" data-log-state="closed">
        <FriendsBar to="/friends" />
        <div className="nf-quiet">
          <p className="gr-quiet-title">No groups for you yet</p>
          <p>Groups open up based on what you’ve talked about with Ember.</p>
          <Link to="/friends" className="ember-btn ember-btn-secondary" data-log="group_list_back">Back to Meet people</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="nf-page gr-page" data-log-seen="group_list" data-log-state="open">
      <FriendsBar to="/friends" />
      <div className="nf-head">
        <h1 className="nf-title">Groups</h1>
        <p className="gr-lede">Ask, share, and hear from people who’ve been there. Groups open up based on what you’ve talked about with Ember.</p>
      </div>
      <div className="nf-main">
        {list.groups.length > 0 && (
          <>
            <div className="nf-label"><span>OPEN TO YOU</span></div>
            <div className="gr-rows">
              {list.groups.map((g, i) => {
                const [ground, ink] = GROUP_TINTS[i % GROUP_TINTS.length];
                const to = `/groups/${encodeURIComponent(g.id)}`;
                const sub = g.joined
                  ? `You’re in${g.fresh > 0 ? ` · ${g.fresh} new` : ''}`
                  : [g.people > 0 ? plural(g.people, 'person', 'people') : 'Be the first one here', g.week > 0 ? `${g.week} new this week` : ''].filter(Boolean).join(' · ');
                const tile = <span className="gr-tile" style={{ background: ground }}><GroupGlyph ink={ink} /></span>;
                return g.joined ? (
                  <Link to={to} className="gr-row" key={g.id} data-log="group_open" data-log-state="joined" data-log-match-id={g.id}>
                    {tile}
                    <span className="nf-row-text">
                      <span className="nf-row-name" dir="auto">{g.name}</span>
                      <span className={`nf-row-sub${g.fresh > 0 ? ' is-ink' : ''}`}>{sub}</span>
                    </span>
                    <span className="gr-row-chev">{CHEVRON}</span>
                  </Link>
                ) : (
                  <div className="gr-row" key={g.id}>
                    {tile}
                    <Link to={to} className="nf-row-text gr-row-link" data-log="group_open" data-log-state="not_joined" data-log-match-id={g.id}>
                      <span className="nf-row-name" dir="auto">{g.name}</span>
                      <span className="nf-row-sub">{sub}</span>
                    </Link>
                    <button type="button" className="gr-join" disabled={Boolean(joining)} data-log="group_join" data-log-state="list" data-log-match-id={g.id} onClick={() => { void join(g.id); }}>
                      Join
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {list.locked.length > 0 && (
          <>
            <div className="nf-label is-spaced"><span>NOT OPEN FOR YOU YET</span></div>
            <ul className="gr-locked">
              {list.locked.map((g) => (
                <li key={g.id}>
                  <button type="button" className="gr-locked-row" data-log="group_locked_open" data-log-match-id={g.id} onClick={() => setLocked(g)}>
                    <span className="gr-locked-icon">{LOCK}</span>
                    <span className="gr-locked-name" dir="auto">{g.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {locked && (
        <BottomSheet
          title={locked.name}
          className="gr-sheet"
          description={(
            <>
              <span className="gr-sheet-line">Each group is for people who are living it. We haven’t heard about this from you yet.</span>
              <span className="gr-sheet-line">If it’s part of your life, tell Ember what you’ve been through. Once Ember understands, this group opens for you.</span>
            </>
          )}
          logSeen="group_locked"
          logSeenAttrs={{ 'data-log-match-id': locked.id }}
          onClose={() => setLocked(null)}
        >
          <Link to="/talk" className="ember-btn ember-btn-primary" data-log="group_locked_talk" data-log-match-id={locked.id}>Talk to Ember</Link>
          <button type="button" className="ember-btn ember-btn-ghost" data-log="group_locked_close" onClick={() => setLocked(null)}>Got it</button>
        </BottomSheet>
      )}
    </main>
  );
}
